import { queryOptions } from "@tanstack/react-query";

import { apiUrl } from "@workspace/base-api/apiUrl";
import {
	fetchJson,
	toApiError,
	type HttpRequestError,
} from "@workspace/base-api/fetchJson";
import { toCamel } from "@workspace/shared";

import type { VenueDeviceHealthResponse } from "./health/types";
import type {
	DeactivateDeviceResponse,
	Device,
	DeviceLastActivity,
	DeviceListResponse,
	DeviceRebootImpact,
	RebootDeviceResponse,
	ReceiptPrintersResponse,
	RegisterDeviceInput,
	RegisterDeviceResponse,
	UpdateDeviceInput,
	UpdateDeviceResponse,
} from "./types";

export const deviceManagementApi = {
	/**
	 * Get all devices for a tenant, optionally filtered by venue
	 */
	async getDevices(
		tenant: string,
		venueId?: string,
	): Promise<DeviceListResponse> {
		const url = new URL("/venue-management/devices", apiUrl);

		if (venueId) {
			url.searchParams.set("venueId", venueId);
		}

		return await fetchJson<DeviceListResponse>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},

	/**
	 * Query options for getting devices list
	 */
	getDevicesOptions: (tenant: string, venueId?: string) => {
		return queryOptions({
			queryKey: [tenant, "devices", "list", venueId ?? "all"],
			queryFn: () => deviceManagementApi.getDevices(tenant, venueId),
		});
	},

	/**
	 * HA-1027: the printers a Staff Terminal can take as its Receipt Printer, the same list a queue's Add printer offers at its venue.
	 */
	async getReceiptPrinters(
		tenant: string,
		deviceId: string,
	): Promise<ReceiptPrintersResponse> {
		const url = new URL(
			`/venue-management/devices/${deviceId}/receipt-printers/available`,
			apiUrl,
		);

		return await fetchJson<ReceiptPrintersResponse>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});
	},

	getReceiptPrintersOptions: (tenant: string, deviceId: string) =>
		queryOptions({
			queryKey: [tenant, "devices", deviceId, "receipt-printers", "available"],
			queryFn: () => deviceManagementApi.getReceiptPrinters(tenant, deviceId),
		}),

	/**
	 * HA-561: device + printer health for a venue's admin "Devices" surface.
	 */
	async getVenueDeviceHealth(
		tenant: string,
		venueId: string,
	): Promise<VenueDeviceHealthResponse> {
		const url = new URL(
			`/venue-management/venues/${venueId}/device-health`,
			apiUrl,
		);

		return await fetchJson<VenueDeviceHealthResponse>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},

	/**
	 * Query options for venue device health. The 30s poll is baked in so every consumer
	 * (HA-562/563/564 mutations invalidating [tenant,"devices","health"]) refreshes identically.
	 */
	getVenueDeviceHealthOptions: (tenant: string, venueId: string) => {
		return queryOptions({
			queryKey: [tenant, "devices", "health", venueId],
			queryFn: () => deviceManagementApi.getVenueDeviceHealth(tenant, venueId),
			refetchInterval: 30_000,
		});
	},

	/**
	 * HA-576: in-flight job count for the reboot confirm dialog. Deliberately available while the
	 * device is offline — the reboot itself is refused, but the cost is still worth showing.
	 */
	async getRebootImpact(
		tenant: string,
		deviceId: string,
	): Promise<DeviceRebootImpact> {
		const url = new URL(
			`/venue-management/devices/${deviceId}/reboot-impact`,
			apiUrl,
		);

		return await fetchJson<DeviceRebootImpact>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},

	getRebootImpactOptions: (
		tenant: string,
		deviceId: string,
		enabled: boolean,
	) =>
		queryOptions({
			queryKey: [tenant, "devices", deviceId, "reboot-impact"],
			queryFn: () => deviceManagementApi.getRebootImpact(tenant, deviceId),
			retry: false,
			// The count is stated in the dialog copy, so it must reflect the moment the dialog opened.
			staleTime: 0,
			gcTime: 0,
			enabled,
		}),

	/**
	 * HA-576: dispatch a remote reboot. Success means the proxy accepted it — the device goes down
	 * about a second later.
	 */
	async rebootDevice(tenant: string, deviceId: string) {
		const url = new URL(`/venue-management/devices/${deviceId}/reboot`, apiUrl);

		try {
			const response = await fetchJson<RebootDeviceResponse>(url.toString(), {
				method: "POST",
				headers: {
					tenant: tenant || "",
				},
				body: JSON.stringify({}),
			});

			return { success: true as const, response };
		} catch (e: unknown) {
			return await toApiError(e, "Failed to reboot device");
		}
	},

	rebootDeviceOptions: (tenant: string, deviceId: string) => ({
		mutationKey: [tenant, "devices", deviceId, "reboot"],
		mutationFn: () => deviceManagementApi.rebootDevice(tenant, deviceId),
	}),

	/**
	 * HA-350: revoke a lost or stolen device's access. Idempotent — re-firing on an already
	 * deactivated device succeeds and reports `alreadyInactive`.
	 */
	async deactivateDevice(tenant: string, deviceId: string, reason?: string) {
		const url = new URL(
			`/venue-management/devices/${deviceId}/deactivate`,
			apiUrl,
		);

		try {
			const response = await fetchJson<DeactivateDeviceResponse>(
				url.toString(),
				{
					method: "POST",
					headers: { tenant: tenant || "" },
					body: JSON.stringify({ reason: reason?.trim() || null }),
				},
			);

			return { success: true as const, response };
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false as const,
				code: error.problem?.code as string | undefined,
				error: error.message,
			};
		}
	},

	deactivateDeviceOptions: (tenant: string, deviceId: string) => ({
		mutationKey: [tenant, "devices", deviceId, "deactivate"],
		mutationFn: (reason?: string) =>
			deviceManagementApi.deactivateDevice(tenant, deviceId, reason),
	}),

	/**
	 * HA-350 / D-HA-1 D7: last-known activity for the deactivation dialog. Read `source` with `at` —
	 * a till has no heartbeat, so its timestamp is a registration date, not a sighting.
	 */
	async getDeviceLastActivity(
		tenant: string,
		deviceId: string,
	): Promise<DeviceLastActivity> {
		const url = new URL(
			`/venue-management/devices/${deviceId}/last-activity`,
			apiUrl,
		);

		return await fetchJson<DeviceLastActivity>(url.toString(), {
			method: "GET",
			headers: { tenant: tenant || "" },
		});
	},

	deviceLastActivityOptions: (
		tenant: string,
		deviceId: string,
		enabled: boolean,
	) =>
		queryOptions({
			queryKey: [tenant, "devices", deviceId, "last-activity"],
			queryFn: () =>
				deviceManagementApi.getDeviceLastActivity(tenant, deviceId),
			retry: false,
			enabled,
		}),

	/**
	 * Get a single device by ID
	 */
	async getDevice(tenant: string, deviceId: string): Promise<Device> {
		const url = new URL(`/venue-management/devices/${deviceId}`, apiUrl);

		return await fetchJson<Device>(url.toString(), {
			method: "GET",
			headers: {
				tenant: tenant || "",
			},
		});
	},

	/**
	 * Query options for getting a single device
	 */
	getDeviceOptions: (tenant: string, deviceId: string) => {
		return queryOptions({
			queryKey: [tenant, "devices", deviceId],
			queryFn: () => deviceManagementApi.getDevice(tenant, deviceId),
		});
	},

	/**
	 * Register a new device
	 */
	async registerDevice(tenant: string, data: RegisterDeviceInput) {
		const url = new URL("/venue-management/devices", apiUrl);

		try {
			const response = await fetchJson<RegisterDeviceResponse>(url.toString(), {
				method: "POST",
				headers: {
					tenant: tenant || "",
				},
				body: JSON.stringify(data),
			});

			return {
				success: true as const,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false as const,
				details: toCamel(await error.response.json()) as Record<
					string,
					unknown
				>,
				error: error.message,
			};
		}
	},

	/**
	 * Mutation options for registering a device
	 */
	registerDeviceOptions: (tenant: string) => {
		return {
			mutationKey: [tenant, "devices", "register"],
			mutationFn: (data: RegisterDeviceInput) =>
				deviceManagementApi.registerDevice(tenant, data),
		};
	},

	/**
	 * Update a device
	 */
	async updateDevice(
		tenant: string,
		deviceId: string,
		data: UpdateDeviceInput,
	) {
		const url = new URL(`/venue-management/devices/${deviceId}`, apiUrl);

		try {
			const response = await fetchJson<UpdateDeviceResponse>(url.toString(), {
				method: "PATCH",
				headers: {
					tenant: tenant || "",
				},
				body: JSON.stringify(data),
			});

			return {
				success: true as const,
				response,
			};
		} catch (e: unknown) {
			const error = e as HttpRequestError;
			return {
				success: false as const,
				details: toCamel(await error.response.json()) as Record<
					string,
					unknown
				>,
				error: error.message,
			};
		}
	},

	/**
	 * Mutation options for updating a device
	 */
	updateDeviceOptions: (tenant: string, deviceId: string) => {
		return {
			mutationKey: [tenant, "devices", deviceId, "update"],
			mutationFn: (data: UpdateDeviceInput) =>
				deviceManagementApi.updateDevice(tenant, deviceId, data),
		};
	},

	/**
	 * Unregister/delete a device
	 */
	async unregisterDevice(tenant: string, deviceId: string, reason?: string) {
		const url = new URL(`/venue-management/devices/${deviceId}`, apiUrl);

		try {
			const response = await fetchJson<{ message: string }>(url.toString(), {
				method: "DELETE",
				headers: {
					tenant: tenant || "",
				},
				body: JSON.stringify({ reason }),
			});

			return {
				success: true as const,
				response,
			};
		} catch (e: unknown) {
			return await toApiError(e, "Failed to unregister device");
		}
	},

	/**
	 * Mutation options for unregistering a device
	 */
	unregisterDeviceOptions: (tenant: string, deviceId: string) => {
		return {
			mutationKey: [tenant, "devices", deviceId, "unregister"],
			mutationFn: (reason?: string) =>
				deviceManagementApi.unregisterDevice(tenant, deviceId, reason),
		};
	},
};
